import { normalizeRotationDegrees } from '@/lib/transforms/geometry'
import type { MultiRotateInitials } from './types/multi-rotate-initials'
import type { RotatableItemSnapshot } from './types/rotatable-item-snapshot'
import type { MultiRotateNextGeometry } from './types/multi-rotate-next-geometry'

export function buildMultiRotateInitials(items: RotatableItemSnapshot[]): MultiRotateInitials {
  return Object.fromEntries(
    items.map((item) => [
      item.id,
      {
        x: item.x,
        y: item.y,
        width: item.width,
        height: item.height,
        rotation: item.rotation ?? 0,
      },
    ])
  )
}

export function computeMultiRotatePreview(params: {
  initials: MultiRotateInitials
  pivot: { x: number; y: number }
  deltaDegrees: number
}): Record<string, MultiRotateNextGeometry> {
  const { initials, pivot, deltaDegrees } = params
  const deltaRad = (deltaDegrees * Math.PI) / 180
  const cos = Math.cos(deltaRad)
  const sin = Math.sin(deltaRad)
  const updates: Record<string, MultiRotateNextGeometry> = {}

  for (const [id, init] of Object.entries(initials)) {
    const icx = init.x + init.width / 2
    const icy = init.y + init.height / 2
    const dx = icx - pivot.x
    const dy = icy - pivot.y
    const ncx = pivot.x + dx * cos - dy * sin
    const ncy = pivot.y + dx * sin + dy * cos

    updates[id] = {
      x: ncx - init.width / 2,
      y: ncy - init.height / 2,
      rotation: normalizeRotationDegrees(init.rotation + deltaDegrees),
    }
  }

  return updates
}

export function collectChangedMultiRotateCommits(params: {
  initials: MultiRotateInitials
  currentById: Record<string, { x: number; y: number; rotation?: number }>
  positionEpsilon?: number
  rotationEpsilon?: number
}): Array<{
  id: string
  before: { x: number; y: number; rotation: number }
  after: { x: number; y: number; rotation: number }
}> {
  const { initials, currentById, positionEpsilon = 1e-4, rotationEpsilon = 1e-2 } = params
  const commits: Array<{
    id: string
    before: { x: number; y: number; rotation: number }
    after: { x: number; y: number; rotation: number }
  }> = []

  for (const [id, before] of Object.entries(initials)) {
    const current = currentById[id]

    if (!current) continue
    const currentRot = normalizeRotationDegrees(current.rotation ?? 0)
    const beforeRot = normalizeRotationDegrees(before.rotation)
    const changed =
      Math.abs(current.x - before.x) > positionEpsilon ||
      Math.abs(current.y - before.y) > positionEpsilon ||
      Math.abs(currentRot - beforeRot) > rotationEpsilon

    if (!changed) continue

    commits.push({
      id,
      before: { x: before.x, y: before.y, rotation: before.rotation },
      after: { x: current.x, y: current.y, rotation: current.rotation ?? 0 },
    })
  }

  return commits
}
